/**
 * ================================================================================================
 *  PLATFORM RELEASE MANAGEMENT — WHERE AN UPDATE IS ACTUALLY PERFORMED
 * ================================================================================================
 *
 * Addendum §9.1(5): the authorized operator *"sees current and target versions, changes and
 * material deployment impact, and confirms the update once in the application."* §9.1(6): *"About
 * shows the verified installed identity."* §9.1(7): *"a distinct explicit Owner acceptance action,
 * bound to the exact tested artifact hash and installed deployment record."*
 *
 * ── THIS FILE USED TO ARGUE AGAINST ITSELF ──────────────────────────────────────────────────────
 *
 * An earlier version rendered a NOTICE and nothing else, and said in its own header that a control
 * here *"would be a browser performing a deployment"*. That was a position written into code
 * against a clause the code was supposed to implement, and §9.3 answers it in one line: *"A local
 * launcher alone is an increment, not completion of the full About-based workflow."* The notice was
 * the increment. This is the workflow.
 *
 * ── AND NO DEPLOYMENT CREDENTIAL IS ANYWHERE NEAR IT ────────────────────────────────────────────
 *
 * §9.2: *"No deployment credentials in the browser."* Pressing the button here POSTs an artifact
 * hash. The server records an authorization and a request; a privileged agent on the application
 * host decides whether to act on them, re-deriving every precondition from a registry this
 * application cannot write. The browser cannot deploy, cannot name a host, cannot choose bytes that
 * were not published, and cannot skip the review — it can only ask for one of the things the estate
 * has already agreed it may become.
 *
 * ── WHAT THE OPERATOR SEES BEFORE THEY PRESS IT ─────────────────────────────────────────────────
 *
 * Current and target version, the artifact hash, the review that admitted it, and the material
 * impact — which, for a routine update, is the one sentence that matters: it applies no database
 * migration, because an artifact that would is refused by the agent and belongs on the full
 * deployment path. That is not reassurance; it is the property that makes the stated rollback
 * complete, and the rollback is stated too.
 */

import { useCallback, useEffect, useState } from 'react';

import { callOperation } from '../api/operations';
import type { DeploymentRecordView, ReleaseOverview } from '../api/contract';
import { useLocale } from '../i18n/LocaleProvider';

export interface ReleaseNoticeProps {
  /** The server's answer, or `null` when it has not been asked yet or refused the caller. */
  readonly release: ReleaseOverview | null;
  /** Re-reads the overview. Called after an action so the screen stops showing a stale state. */
  readonly onChanged?: () => void;
}

type Busy = 'idle' | 'starting' | 'accepting';

/** The newest deployment, which is the one every statement on this panel is about. */
function latest(deployments: readonly DeploymentRecordView[]): DeploymentRecordView | null {
  return deployments[0] ?? null;
}

export function ReleaseNotice({ release, onChanged }: ReleaseNoticeProps): JSX.Element | null {
  const { t } = useLocale();
  const [busy, setBusy] = useState<Busy>('idle');
  const [failure, setFailure] = useState<string | null>(null);
  const [evidence, setEvidence] = useState('');

  const available = release?.availability.available ?? null;
  const deployments = release?.deployments ?? [];
  const current = latest(deployments);

  /*
   * WHILE A DEPLOYMENT IS IN FLIGHT, THIS PANEL RE-READS ITSELF.
   *
   * The operator pressed a button and is watching the screen; the agent runs on a timer. Without
   * this the panel would say `requested` until the dialog was closed and reopened, which reads as
   * nothing having happened — the exact impression a deployment control must never give. It polls
   * only while there is something to watch, and stops the moment the state is terminal.
   */
  const inFlight = current !== null && (current.state === 'requested' || current.state === 'running');
  useEffect(() => {
    if (!inFlight || onChanged === undefined) return undefined;
    const timer = setInterval(onChanged, 5000);
    return () => clearInterval(timer);
  }, [inFlight, onChanged]);

  /*
   * WHETHER THIS UPDATE CHANGES THE DATABASE, as the SERVER measured it.
   *
   * Never computed here. The comparison is between what the release carries and what the DATABASE
   * has applied, and only one side of that is visible to a browser -- a client that decided this
   * for itself would be deciding, on no evidence, whether a migration is about to run.
   */
  const schemaChange = release?.candidateSchema.schemaChange === true;
  const pending = release?.candidateSchema.pending ?? null;
  const schemaStated = release?.candidateSchema.stated === true;

  const startUpdate = useCallback((): void => {
    if (available === null) return;
    setBusy('starting');
    setFailure(null);
    void callOperation('platform.release.update.start', {
      /*
       * ONE CLICK, AND IT CARRIES THE ACKNOWLEDGEMENT. **Owner decision, 2026-10-19.**
       *
       * The server measured whether this release changes the database and refuses without the
       * acknowledgement; the button above says so in words before it is pressed. Sending it
       * unconditionally would make the server's refusal unreachable, so it is sent only when the
       * server itself reported a schema change -- the client acknowledges what it was told, and
       * never asserts it on its own.
       */
      body: {
        artifactSha256: available.artifactSha256,
        ...(schemaChange ? { acknowledgeSchemaChange: true } : {}),
      },
    }).then(
      () => {
        setBusy('idle');
        onChanged?.();
      },
      (error: unknown) => {
        setBusy('idle');
        // THE SERVER'S REFUSAL, NOT A SENTENCE OF OUR OWN. `already-active` and `unknown-artifact`
        // are different facts and the operator is the person who has to tell them apart.
        setFailure(error instanceof Error ? error.message : String(error));
      },
    );
  }, [available, onChanged]);

  const accept = useCallback((): void => {
    if (current === null) return;
    setBusy('accepting');
    setFailure(null);
    void callOperation('platform.release.accept', {
      body: { deploymentUid: current.deploymentUid, evidence },
    }).then(
      () => {
        setBusy('idle');
        setEvidence('');
        onChanged?.();
      },
      (error: unknown) => {
        setBusy('idle');
        setFailure(error instanceof Error ? error.message : String(error));
      },
    );
  }, [current, evidence, onChanged]);

  // Nothing known at all: not asked yet, or the caller may not ask. Neither is a message.
  if (release === null) return null;

  /*
   * THE ACCEPTANCE ACTION IS OFFERED FOR A VERIFIED DEPLOYMENT THAT IS NOT YET ACCEPTED.
   *
   * It is deliberately NOT tied to `available`: by the time a release has been installed it is no
   * longer the candidate, and an acceptance action that disappeared the moment the update succeeded
   * would make §9.1(7) unreachable in practice.
   */
  const acceptable = current !== null && current.state === 'verified' && !release.acceptedHere;

  if (available === null && current === null) return null;

  return (
    <div className="about__update" role="note" data-release-available={available?.tag ?? ''}>
      {available === null ? null : (
        <>
          <p className="about__updateTitle">
            {t('about.updateAvailable')}: {available.tag}
          </p>
          <p className="about__notes">
            {t('about.updateApproval')}: {available.review.reference}
          </p>
          {/*
            THE IMPACT, IN THE ONE SENTENCE THAT DECIDES WHETHER THIS IS ROUTINE. §9.1(5) asks for
            "material deployment impact"; for an application-only update that is: no schema change,
            and therefore a rollback that is a symlink flip rather than a recovery project.
          */}
          <p className="about__notes">{t(schemaChange ? 'about.updateImpactSchema' : 'about.updateImpact')}</p>
          {schemaChange ? (
            /*
              THE DATABASE CHANGE, NAMED BEFORE THE BUTTON IS PRESSED. **Owner decision,
              2026-10-19.** The decision allows a schema change in this path WITH explicit recovery
              limits, so the two sentences the operator needs are here and not in a runbook: a
              fresh backup is proved before anything changes, and after the migration the recovery
              is two moves rather than a symlink flip.
            */
            <p className="about__notes" data-schema-change="yes">
              {schemaStated && pending !== null
                ? `${t('about.updateSchemaPending')}: ${String(pending)}`
                : t('about.updateSchemaUnstated')}
              {' — '}
              {t('about.updateSchemaRecovery')}
            </p>
          ) : null}
          <button
            type="button"
            className="about__updateAction"
            disabled={busy !== 'idle' || inFlight}
            onClick={startUpdate}
          >
            {busy === 'starting' ? t('about.updateStarting') : t('about.updateConfirm')}
          </button>
        </>
      )}

      {current === null ? null : (
        <p className="about__notes" data-deployment-state={current.state}>
          {t('about.deploymentState')}: {current.releaseTag} — {t(`about.state.${current.state}`)}
          {current.state === 'verified' && current.verifiedTag !== null
            ? ` (${t('about.verifiedIdentity')}: ${current.verifiedTag})`
            : null}
          {current.detail === null ? null : ` — ${current.detail}`}
        </p>
      )}

      {acceptable ? (
        <div className="about__acceptance">
          {/*
            THE OWNER'S ACCEPTANCE, AND IT IS A SEPARATE ACT. §9.1(7). The evidence field is
            required by the server, not merely requested here: an acceptance with no evidence is the
            clause being decorated rather than met.
          */}
          <label className="about__notes" htmlFor="about-acceptance-evidence">
            {t('about.acceptEvidence')}
          </label>
          <input
            id="about-acceptance-evidence"
            type="text"
            value={evidence}
            onChange={(event) => setEvidence(event.target.value)}
          />
          <button
            type="button"
            className="about__acceptAction"
            disabled={busy !== 'idle' || evidence.trim() === ''}
            onClick={accept}
          >
            {busy === 'accepting' ? t('about.acceptWorking') : t('about.acceptAction')}
          </button>
        </div>
      ) : null}

      {release.acceptedHere ? <p className="about__notes">{t('about.acceptedAlready')}</p> : null}
      {failure === null ? null : (
        <p className="about__mismatch" role="alert">
          {failure}
        </p>
      )}
    </div>
  );
}
